import moment from 'moment';

export const INITIAL_STATE = {
  branches: [],
  speakers: [],
  studyMethods: [],
  studySchedules: [],
  cities: [],
  venues: [],
  isInHotelTrainingSelected: false,
  selectedSpeakerId: 'none',
  speakerForm: false,
  priceInputModalOpen: false,
  promoInputModalOpen: false,
  selectedPrice: {},
  selectedPromo: {},
};

// Fields parsed to integers by handleInput
export const INT_FIELDS = [
  'maxSeats',
  'specialQuota',
  'priceQuota',
  'priceQuotaRemaining',
];

// Optional fields that stay valid even when emptied
export const ALWAYS_VALID_FIELDS = [
  'notes',
  'specialInfo',
  'practiceDays',
  'priceTitle',
  'priceTitleDesc',
  'priceInfo',
];

export const DAY_TYPE_OPTIONS = [
  { id: 1, name: 'Weekend' },
  { id: 2, name: 'Weekday' },
];

// Factory (not a module-level constant): field values contain nested
// objects/arrays, and a shared instance would be mutated through state
// updates, breaking form reset. Fresh objects on every call.
export const createInitialForm = () => ({
  branchId: {
    value: 0,
    valid: false,
    invalid: false,
    error: 'Please Select a Branch',
  },
  study_methodId: {
    value: 0,
    valid: false,
    invalid: false,
    error: 'Please Select a Study Method',
  },
  study_scheduleId: {
    value: 0,
    valid: false,
    invalid: false,
    error: 'Please Select a Study Schedule',
  },
  cityId: {
    value: 0,
    valid: false,
    invalid: false,
    error: 'Please Select a City',
  },
  venueId: {
    value: 0,
    valid: false,
    invalid: false,
    error: 'Please Select a Venue',
  },
  dayType: {
    value: '',
    valid: true,
    invalid: false,
    error: 'Please select a Day Type',
    help: 'Optional. ex.) Weekend',
  },
  regionType: {
    value: '',
    valid: false,
    invalid: false,
    error: 'Please select a Region Type',
    help: 'Optional. ex.) Local',
  },
  content: {
    value: '',
    valid: false,
    invalid: false,
    error: 'Please input content',
  },
  notes: {
    value: '',
    valid: true,
    invalid: false,
    error: 'Please input notes',
  },
  price: {
    value: 0,
    displayValue: '0',
    valid: true,
    invalid: false,
    isFree: false,
    error: 'Input price',
  },
  specialPrice: {
    value: 0,
    displayValue: '0',
    valid: true,
    invalid: false,
    isFree: false,
    error: 'Input special price',
  },
  earlyPrice: {
    value: 0,
    displayValue: '0',
    valid: true,
    invalid: false,
    isFree: false,
    error: 'Input early bird price',
  },
  termPrice: {
    value: 0,
    displayValue: '0',
    valid: true,
    invalid: false,
    isFree: false,
    error: 'Input term price',
  },
  term2Price: {
    value: 0,
    displayValue: '0',
    valid: true,
    invalid: false,
    isFree: false,
    error: 'Input term 2 price',
  },
  normalPromoPrice: {
    value: 0,
    displayValue: '0',
    valid: true,
    invalid: false,
    isFree: false,
    error: 'Input normal promo price',
  },
  corporatePrice: {
    value: 0,
    displayValue: '0',
    valid: true,
    invalid: false,
    isFree: false,
    error: 'Input corporate price',
  },
  registrationFee: {
    value: 0,
    displayValue: '0',
    valid: true,
    invalid: false,
    isFree: false,
    error: 'Input registration fee',
  },
  speakerIds: {
    value: [],
    valid: true,
    invalid: false,
    error: 'Please pick at least 1 speaker',
  },
  maxSeats: {
    value: 0,
    valid: false,
    invalid: false,
    error: 'Please input max seats',
  },
  specialQuota: {
    value: 0,
    valid: true,
    invalid: false,
    error: 'Please input special price quota',
  },
  specialDesc: {
    value: '',
    valid: true,
    invalid: false,
    error: 'Please input special price description',
    help: 'ex.) Powered by Purwadhika',
  },
  specialInfo: {
    value: '',
    valid: true,
    invalid: false,
    error: 'Please input special price information',
    help: 'ex.) *S&K berlaku',
  },
  priceTitle: {
    value: '',
    valid: true,
    invalid: false,
    error: 'Please input price title',
    help: 'Optional. ex.) Launching Promo',
  },
  priceTitleDesc: {
    value: '',
    valid: true,
    invalid: false,
    error: 'Please input price title description',
    help: 'Optional. ex.) Powered by Purwadhika',
  },
  priceQuota: {
    value: 0,
    valid: true,
    invalid: false,
    error: 'Please input price quota',
    help: 'Optional. Will be displayed as "Promo ini hanya untuk X Pendaftar"',
  },
  priceQuotaRemaining: {
    value: 0,
    valid: true,
    invalid: false,
    error: 'Please input price remaining quota',
    help: 'Optional. Will be displayed as “Kuota Promo Hanya Tersisa X Lagi”',
  },
  priceInfo: {
    value: '',
    valid: true,
    invalid: false,
    error: 'Please input price information',
    help: 'Optional. ex.) *S&K berlaku',
  },
  startDate: {
    value: moment().startOf('date').toDate(),
    valid: true,
    invalid: false,
    error: 'Please select Start Date',
  },
  endDate: {
    value: moment().startOf('date').toDate(),
    valid: true,
    invalid: false,
    error: 'Please select End Date',
  },
  specialStartDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Special Price Start Date',
  },
  specialEndDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Special Price End Date',
  },
  earlyStartDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Early Bird Start Date',
  },
  earlyEndDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Early Bird End Date',
  },
  termStartDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Term Start Date',
  },
  termEndDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Term End Date',
  },
  term2StartDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Term 2 Start Date',
  },
  term2EndDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Term 2 End Date',
  },
  normalPromoStartDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Normal Promo Start Date',
  },
  normalPromoEndDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Normal Promo End Date',
  },
  corporateSaleStartDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Corporate Sale Start Date',
  },
  corporateSaleEndDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Corporate Sale End Date',
  },
  saleEndDate: {
    value: null,
    valid: false,
    invalid: false,
    error: 'Please select Sale End Date',
  },
  startTime: {
    value: '09:00',
    valid: true,
    invalid: false,
    error: 'Please select Start Time',
  },
  endTime: {
    value: '16:00',
    valid: true,
    invalid: false,
    error: 'Please select End Time',
  },
  practiceStartTime: {
    value: '09:00',
    valid: true,
    invalid: false,
    error: 'Please select Practice Session Start Time',
  },
  practiceEndTime: {
    value: '16:00',
    valid: true,
    invalid: false,
    error: 'Please select Practice Session End Time',
  },
  days: {
    value: '',
    valid: false,
    invalid: false,
    help: "ex.) 'Tuesday', 'Mon-Fri, 'Tue, Thu'",
    error: 'Please input Training Session Days',
  },
  practiceDays: {
    value: '',
    valid: true,
    invalid: false,
    help:
      'Optional. Only input if Practice Session Days is different from Training Session Days.',
    additionalHelp: "ex.) 'Tuesday', 'Mon-Fri, 'Tue, Thu'.",
  },
  code: {
    value: '',
    valid: false,
    invalid: false,
    help: "ex.) 'JC-XX001",
    error: 'Please input code',
  },
  audience: {
    value: [{ item: '' }],
    valid: true,
    invalid: false,
    error: 'Input audience',
  },
  syllabus: {
    value: [{ item: '' }],
    valid: true,
    invalid: false,
    error: 'Input syllabus',
  },
  programPrices: {
    value: [],
    invalid: false,
  },
  programAdditionalPromos: {
    value: [],
    invalid: false,
  },
});
